import { apiClient } from '../../../api';
import { Path } from '../../../api/backendUrl';
import { appConfig } from '../../../config/appConfig';

const env = appConfig.app.isProduction;

let BASE_URL = env
  ? import.meta.env.VITE_APP_BACKEND_AUTH
  : 'http://localhost:3010/user/api/v1/';

export type ProviderPortalTourOutcome = 'completed' | 'skipped';

/** Persist provider homepage onboarding tour (finish or skip) — same auth service as sessionInfo. */
export const updateProviderPortalOnboardingTour = async (
  outcome: ProviderPortalTourOutcome
) => {
  try {
    const res = await apiClient.patch(
      `${BASE_URL}/auth/${Path.authSessionProviderPortalTour}`,
      { outcome }
    );
    return res.data as {
      success: boolean;
      message?: string;
      providerPortalTour: {
        dismissed: boolean;
        outcome: string | null;
        updatedAt: string | null;
      };
    };
  } catch (error) {
    console.error('Error updating provider portal onboarding tour: ', error);
    throw error;
  }
};

/**
 * Redirect to the customer Cognito login. When `returnUrl` is given it is sent
 * as a query param for the auth service to echo back through the OAuth state,
 * so the post-login redirect lands back on the originating page (pay page).
 */
export const handleLogin = async (returnUrl?: string) => {
  try {
    const params = new URLSearchParams({ portal: 'customer', client: 'web' });
    if (returnUrl) params.append('returnUrl', returnUrl);
    window.location.href = `${BASE_URL}/auth/web/login?${params.toString()}`;
  } catch (error) {
    console.error('Login failed', error);
  }
};

/**
 * Log in against the provider portal. Because the user already holds a Cognito
 * SSO session, this re-authenticates silently, rebuilds the session with the
 * `provider` role, and the backend callback lands them on the provider
 * dashboard. Used right after a user becomes a provider so they arrive in the
 * portal with a fresh session instead of a stale `customer` one.
 */
export const handleProviderLogin = async () => {
  try {
    window.location.href = `${BASE_URL}/auth/web/login?portal=provider&client=web`;
  } catch (error) {
    console.error('Provider login failed', error);
  }
};

export const handleLogout = async () => {
  try {
    localStorage.setItem('logoutState', 'true');
    window.location.href = `${BASE_URL}/auth/logout?portal=customer&client=web`;
  } catch (err) {
    localStorage.removeItem('logoutState');
    console.error('Logout failed', err);
  }
};

/**
 * Redirect to the customer Cognito registration. Same optional `returnUrl`
 * contract as handleLogin — used so an unregistered customer who registers
 * from the booking pay page lands back on it after the callback.
 */
export const handleRegistration = async (returnUrl?: string) => {
  try {
    const params = new URLSearchParams({ portal: 'customer', client: 'web' });
    if (returnUrl) params.append('returnUrl', returnUrl);
    window.location.href = `${BASE_URL}/auth/web/register?${params.toString()}`;
  } catch (err) {
    console.error('Registration failed', err);
  }
};

export const handleRemoveSession = async () => {
  try {
    const t = new Date().getTime();
    const res = await apiClient.get(`${BASE_URL}/auth/signOut?t=${t} `);
    return res;
  } catch (err) {
    console.error('Logout failed', err);
  }
};

export const getUserBySessionId = async () => {
  try {
    const res = await apiClient.get(
      `${BASE_URL}/auth/me?portal=customer&client=web`
    );

    return res?.data?.sessionInfo;
  } catch (error: any) {
    console.error('Error get user by session  id: ', error);
    throw error;
  }
};

export const checkSessionStatus = async () => {
  try {
    const res = await apiClient.get(
      `${BASE_URL}/auth/session/status?portal=customer&client=web`
    );

    return res?.data;
  } catch (error: any) {
    console.error('Error get session status: ', error);
    throw error;
  }
};
